import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { messageService, agentService } from '../services/mailboxService';
import { useAuth } from '../context/AuthContext';
import { useSync } from '../context/SyncContext';
import { useAccounts } from '../context/AccountContext';
import { safeHtml } from '../utils/sanitizeHtml';

const PAGE_SIZE = 50;

// Build the "to X, Y · cc Z" line for the open message. For a multi-account
// inbox the user needs to see WHICH address received the email, so we never
// collapse to "me" — we always show the bare email address.
function buildRecipientLine(message, allMyEmails) {
  if (!message) return '';
  const recipients = Array.isArray(message.recipients) ? message.recipients : [];

  const isType = (r, num, name) => {
    if (r.recipientType === num) return true;
    if (typeof r.recipientType === 'string' && r.recipientType.toLowerCase() === name) return true;
    return false;
  };

  const formatOne = (r) => {
    const addr = (r.emailAddress || r.email || '').trim();
    if (!addr) return null;
    const name = (r.displayName || r.name || '').trim();
    // Always show the email address — never collapse to "me"
    return name ? `${name} <${addr}>` : addr;
  };

  const tos = recipients.filter((r) => isType(r, 1, 'to')).map(formatOne).filter(Boolean);
  const ccs = recipients.filter((r) => isType(r, 2, 'cc')).map(formatOne).filter(Boolean);

  const fallback = (raw) => {
    if (!Array.isArray(raw)) return [];
    return raw.map((s) => {
      const addr = String(s || '').trim();
      if (!addr) return null;
      return addr;
    }).filter(Boolean);
  };

  const tosFinal = tos.length ? tos : fallback(message.to);
  const ccsFinal = ccs.length ? ccs : fallback(message.cc);

  const parts = [];
  if (tosFinal.length) parts.push(`to ${tosFinal.join(', ')}`);
  if (ccsFinal.length) parts.push(`cc ${ccsFinal.join(', ')}`);
  return parts.join(' · ');
}

function InboxPage() {
  const { folderId, messageId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { syncCompletedTick } = useSync();
  const { allEmails, activeMailboxIds, activeMailboxes, resolveFolderForMailbox, getFolderType } = useAccounts();
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [smartReplies, setSmartReplies] = useState([]);
  const [summary, setSummary] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  // Track whether current folder is "Sent" type (folder type 2)
  const [isSentFolder, setIsSentFolder] = useState(false);

  // Sentinel + observer for infinite scroll. We use a callback ref so the
  // observer attaches/detaches whenever the sentinel DOM node appears/disappears
  // (which happens when loading transitions). The loadMessages ref ensures we
  // always call the latest version without stale closures.
  const loadingMoreRef = useRef(false);
  const messagesRef = useRef(messages);
  const totalCountRef = useRef(totalCount);
  const loadMessagesRef = useRef(null);
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  useEffect(() => { totalCountRef.current = totalCount; }, [totalCount]);

  const observerRef = useRef(null);
  const sentinelRef = useCallback((node) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!node) return;
    observerRef.current = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (!entry || !entry.isIntersecting) return;
      if (loadingMoreRef.current) return;
      const haveAll = messagesRef.current.length >= totalCountRef.current && totalCountRef.current > 0;
      if (haveAll) return;
      const nextPage = Math.floor(messagesRef.current.length / PAGE_SIZE) + 1;
      setPage(nextPage);
      loadMessagesRef.current(nextPage, /*replace*/ false);
    }, { root: null, rootMargin: '400px', threshold: 0 });
    observerRef.current.observe(node);
  }, []);

  // Reset the list when the folder or active mailboxes change.
  // Serialize activeMailboxIds for dependency tracking since Set reference identity changes.
  const activeMailboxKey = [...activeMailboxIds].sort().join(',');
  useEffect(() => {
    if (folderId || !messageId) {
      setMessages([]);
      setPage(1);
      loadMessages(1, /*replace*/ true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderId, activeMailboxKey]);

  // Auto-refresh the message list whenever a sync completes. SyncContext
  // bumps `syncCompletedTick` on every "completed" SignalR event, so we
  // simply re-fetch page 1 with replace=true. The initial render emits
  // tick=0 which is skipped to avoid a double-fetch with the folder effect
  // above. User feedback 2026-05-19: sync said "0 new messages" because the
  // UI never re-pulled the list — the messages WERE in the DB.
  useEffect(() => {
    if (syncCompletedTick > 0) {
      setMessages([]);
      setPage(1);
      loadMessages(1, /*replace*/ true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncCompletedTick]);

  useEffect(() => {
    if (messageId && messageId !== 'undefined') {
      loadMessage(messageId);
    }
  }, [messageId]);

  const loadMessages = useCallback(async (pageToLoad = 1, replace = false) => {
    if (replace) setLoading(true); else setLoadingMore(true);
    loadingMoreRef.current = true;
    try {
      const mailboxIds = [...activeMailboxIds];
      let allMessages = [];
      let total = 0;

      // Detect Sent folder type from the URL's folderId
      const currentFolderType = folderId ? getFolderType(folderId) : null;
      setIsSentFolder(currentFolderType === 2);

      if (mailboxIds.length <= 1) {
        const params = { folderId, page: pageToLoad, pageSize: PAGE_SIZE };
        if (mailboxIds.length === 1) params.mailboxId = mailboxIds[0];
        const response = await messageService.getMessages(params);
        allMessages = response.messages || [];
        total = response.totalCount || 0;
      } else {
        // Multi-mailbox: resolve equivalent folder for EACH mailbox by type
        const results = await Promise.all(
          mailboxIds.map(mbId => {
            // Resolve the equivalent folder for this mailbox
            const resolvedFolderId = folderId
              ? resolveFolderForMailbox(folderId, mbId) || folderId
              : undefined;
            return messageService.getMessages({
              mailboxId: mbId,
              folderId: resolvedFolderId,
              page: pageToLoad,
              pageSize: PAGE_SIZE,
            }).catch(() => ({ messages: [], totalCount: 0 }));
          })
        );
        for (const r of results) {
          allMessages.push(...(r.messages || []));
          total += r.totalCount || 0;
        }
        allMessages.sort((a, b) => new Date(b.receivedUtc) - new Date(a.receivedUtc));
      }

      setTotalCount(total);
      setMessages((prev) => {
        if (replace) return allMessages;
        const seen = new Set(prev.map((m) => m.messageId || m.messageID));
        const merged = [...prev];
        for (const m of allMessages) {
          const key = m.messageId || m.messageID;
          if (!seen.has(key)) { merged.push(m); seen.add(key); }
        }
        return merged;
      });
    } catch (error) {
      console.error('[Webmail] Failed to load messages:', error);
    } finally {
      if (replace) setLoading(false); else setLoadingMore(false);
      loadingMoreRef.current = false;
    }
  }, [folderId, activeMailboxKey, allEmails, resolveFolderForMailbox, getFolderType]);

  // Keep the ref in sync so the sentinel observer always calls the latest version
  useEffect(() => { loadMessagesRef.current = loadMessages; }, [loadMessages]);

  // (Observer is handled by the sentinelRef callback ref above — no separate effect needed)

  const loadMessage = async (id) => {
    console.log('Loading message:', id);
    try {
      const message = await messageService.getMessage(id);
      console.log('Loaded message:', message);
      if (message) {
        setSelectedMessage(message);
        loadAiFeatures(id);
      } else {
        console.error('Message not found:', id);
      }
    } catch (error) {
      console.error('Failed to load message:', error);
    }
  };

  const loadAiFeatures = async (id) => {
    setAiLoading(true);
    try {
      const [summaryRes, repliesRes] = await Promise.all([
        agentService.getSummary(id).catch(() => ({ summary: '' })),
        agentService.getSmartReplies(id).catch(() => ({ replies: [] }))
      ]);
      setSummary(summaryRes.summary || '');
      setSmartReplies(repliesRes.replies || []);
    } catch (error) {
      console.error('Failed to load AI features:', error);
    } finally {
      setAiLoading(false);
    }
  };

  const handleMessageClick = (message) => {
    // API returns messageID (uppercase ID) due to C# property naming
    const msgId = message?.messageId || message?.messageID;
    console.log('=== MESSAGE CLICKED ===', message);
    console.log('Message ID:', msgId);
    
    if (msgId) {
      // Set the message preview immediately while full message loads
      setSelectedMessage(message);
      // Load full message content
      loadMessage(msgId);
    } else {
      console.error('Message clicked but no messageId/messageID:', message);
    }
  };

  const handleReply = () => {
    if (selectedMessage) {
      const msgId = selectedMessage.messageId || selectedMessage.messageID;
      navigate(`/reply/${msgId}`);
    }
  };

  const handleDelete = async () => {
    if (selectedMessage) {
      try {
        const msgId = selectedMessage.messageId || selectedMessage.messageID;
        await messageService.deleteMessage(msgId);
        setSelectedMessage(null);
        loadMessages();
      } catch (error) {
        console.error('Failed to delete message:', error);
      }
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const getInitials = (name, email) => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return email?.[0]?.toUpperCase() || '?';
  };

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      {/* Message List */}
      <div className="message-list-container">
        <div className="message-list-header">
          <span className="message-list-title">
            {folderId ? 'Messages' : 'Inbox'}
          </span>
          <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
            {totalCount} messages
          </span>
        </div>

        <div className="message-list" data-allow-scroll="true">
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
            </div>
          ) : messages.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📭</div>
              <div className="empty-state-title">No messages</div>
              <div className="empty-state-text">
                Your inbox is empty. New messages will appear here.
              </div>
            </div>
          ) : (
            messages.map((message) => {
              const msgId = message.messageId || message.messageID;
              const selectedId = selectedMessage?.messageId || selectedMessage?.messageID;
              // In Sent folder: show recipient. Otherwise show sender.
              const displayName = isSentFolder
                ? (message.toRecipients || message.recipients?.[0]?.emailAddress || message.from)
                : (message.fromName || message.from);
              return (
              <div
                key={msgId}
                className={`message-item ${!message.isRead ? 'unread' : ''} ${selectedId === msgId ? 'active' : ''}`}
                onClick={() => handleMessageClick(message)}
              >
                <div className="message-from">
                  <span>{isSentFolder ? `To: ${displayName}` : displayName}</span>
                  <span className="message-time">{formatDate(message.receivedUtc)}</span>
                </div>
                <div className="message-subject">{message.subject || '(No Subject)'}</div>
                <div className="message-preview">{message.previewText}</div>
                <div className="message-indicators">
                  {message.triage?.priorityScore > 70 && (
                    <span className="indicator priority-high">High Priority</span>
                  )}
                  {message.hasAttachments && (
                    <span className="indicator has-attachment">📎</span>
                  )}
                  {message.triage?.needsReply && (
                    <span className="indicator needs-reply">Reply needed</span>
                  )}
                </div>
              </div>
              );
            })
          )}
          {/* Infinite-scroll sentinel — when this scrolls into view the
              IntersectionObserver in InboxPage fires loadMessages(nextPage,
              append=true). Rendered even when we have all messages so the
              observer can still re-evaluate on subsequent folder changes. */}
          {!loading && messages.length > 0 && (
            <div
              ref={sentinelRef}
              style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}
            >
              {loadingMore ? (
                <div className="loading-spinner" style={{ margin: '0 auto' }} aria-label="Loading more messages" />
              ) : messages.length >= totalCount && totalCount > 0 ? (
                `All ${totalCount.toLocaleString()} messages loaded`
              ) : (
                `Loaded ${messages.length.toLocaleString()} of ${totalCount.toLocaleString()} — scroll to load more`
              )}
            </div>
          )}
        </div>
      </div>

      {/* Message View */}
      <div className="message-view">
        {selectedMessage ? (
          <>
            <div className="message-view-header">
              <h2 className="message-view-subject">
                {selectedMessage.subject || '(No Subject)'}
              </h2>
              <div className="message-view-meta">
                <div className="sender-avatar">
                  {getInitials(selectedMessage.fromName, selectedMessage.from)}
                </div>
                <div className="sender-info">
                  <div className="sender-name">{selectedMessage.fromName || selectedMessage.from}</div>
                  <div className="sender-email">
                    {selectedMessage.fromName ? `<${selectedMessage.from}>` : ''}{' '}
                    {buildRecipientLine(selectedMessage, allEmails) || ''}
                  </div>
                </div>
                <div className="message-actions">
                  <button className="action-btn primary" onClick={handleReply}>
                    ↩️ Reply
                  </button>
                  <button className="action-btn">
                    ↪️ Forward
                  </button>
                  <button className="action-btn" onClick={handleDelete}>
                    🗑️ Delete
                  </button>
                </div>
              </div>
            </div>

            <div className="message-body">
              <div 
                className="message-body-content"
                dangerouslySetInnerHTML={safeHtml(selectedMessage.bodyHtml || selectedMessage.bodyText?.replace(/\n/g, '<br>') || '')}
              />
            </div>
          </>
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon">📧</div>
            <div className="empty-state-title">Select a message</div>
            <div className="empty-state-text">
              Choose a message from the list to view its contents.
            </div>
          </div>
        )}
      </div>

      {/* AI Assistant Panel */}
      {selectedMessage && (
        <div className="ai-panel">
          <div className="ai-panel-header">
            <div className="ai-panel-icon">✨</div>
            <span className="ai-panel-title">AI Assistant</span>
          </div>

          {/* Summary */}
          <div className="ai-suggestion">
            <div className="ai-suggestion-title">Summary</div>
            <div className="ai-suggestion-content">
              {aiLoading ? 'Analyzing...' : (summary || 'No summary available for this message.')}
            </div>
          </div>

          {/* Smart Replies */}
          <div className="ai-suggestion">
            <div className="ai-suggestion-title">Smart Replies</div>
            {aiLoading ? (
              <div className="ai-suggestion-content">Generating suggestions...</div>
            ) : smartReplies.length > 0 ? (
              smartReplies.map((reply, index) => (
                <div key={index} className="smart-reply" onClick={() => navigate('/compose', { state: { body: reply } })}>
                  {reply}
                </div>
              ))
            ) : (
              <div className="ai-suggestion-content">No suggestions available.</div>
            )}
          </div>

          {/* Priority & Category */}
          {selectedMessage.triage && (
            <div className="ai-suggestion">
              <div className="ai-suggestion-title">Analysis</div>
              <div className="ai-suggestion-content">
                <p><strong>Priority:</strong> {Math.round(selectedMessage.triage.priorityScore)}/100</p>
                <p><strong>Category:</strong> {selectedMessage.triage.category || 'General'}</p>
                {selectedMessage.triage.priorityReason && (
                  <p><strong>Reason:</strong> {selectedMessage.triage.priorityReason}</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default InboxPage;

